import React from 'react';
import styled from 'styled-components/macro';
import { forMinWidth } from '../../../theme/breakpoints';
import { TimelineItem } from '../../../hooks/posts';
import TimelineItemComponent from './TimelineItemComponent';

export const isChildTimeline = (timelineDepth?: number): boolean => timelineDepth && timelineDepth > 0;

export interface TimelineComponentProps {
    timelineItems: TimelineItem[];
    className?: string;
    children?: JSX.Element;
    renderItem?: (timelineItem: TimelineItem) => JSX.Element;
}

export default function TimelineComponent(props: TimelineComponentProps): JSX.Element {
    const { timelineItems, className, children } = props;
    const timelineDepth = props.timelineItems?.[0]?.timelineDepth ?? 0;

    return <Wrapper className={`timeline-item ${className ?? ''}`} timelineDepth={timelineDepth}>
        {timelineDepth > 0 && <Line />}

        {timelineItems?.map((item, index) => (
            <TimelineItemComponent
                key={item.id || index}
                timelineItem={item}
                showConnectingLine={index !== timelineItems.length - 1}
                renderItem={props.renderItem} />
        ))}

        {children}
    </Wrapper>
}

const Wrapper = styled.div<{ timelineDepth: number }>`
    /* Important to use 0rem instead of 0 for variables which are used later in calc */
    --timeline-left-margin: 0rem;
    --timeline-logo-width: ${props => isChildTimeline(props.timelineDepth) ? "1rem" : "2.4rem"};
    --timeline-logo-font-size: ${props => isChildTimeline(props.timelineDepth) ? "0.5rem" : "0.8rem"};
    --timeline-logo-to-body-gap: 0.6rem;
    --timeline-items-vertical-gap: ${props => isChildTimeline(props.timelineDepth) ? "1.5rem" : "2.5rem"};
    --timeline-line-width: ${props => isChildTimeline(props.timelineDepth) ? ".1rem" : ".2rem"};
    --timeline-line-color: var(--color-on-surface-lightest);
    --timeline-header-min-height: 3.8rem;
    --timeline-logo-pointer-size: .6rem;
    --timeline-logo-top-offset: -0.4rem;
    --timeline-logo-pointer-border-color: var(--color-on-surface-light);

    /* --timeline-logo-panel-width: calc(var(--timeline-left-margin) + var(--timeline-logo-width) + var(--timeline-logo-to-body-gap)); */
    --timeline-logo-panel-width: calc(var(--timeline-logo-width) + var(--timeline-logo-to-body-gap));
    --timeline-logo-top-margin: calc(var(--timeline-header-min-height) / 2 - var(--timeline-logo-width) / 2 + var(--timeline-logo-top-offset));

    display: flex;
    flex-direction: column;
    background-color: transparent;
    padding-top: ${props => isChildTimeline(props.timelineDepth) ? "var(--timeline-items-vertical-gap)" : "0rem"};

    >:not(:last-child) {
        padding-bottom: var(--timeline-items-vertical-gap);
    }

    ${forMinWidth.sm`
        --timeline-logo-to-body-gap: ${props => isChildTimeline(props.timelineDepth) ? "1rem" : "1.4rem"};
    `}

    ${forMinWidth.md`
        --timeline-left-margin: ${props => props.timelineDepth === 1 ? "1rem" : "0rem"};
        --timeline-logo-width: ${props => isChildTimeline(props.timelineDepth) ? "1.8rem" : "2.8rem"};
        --timeline-logo-font-size: ${props => isChildTimeline(props.timelineDepth) ? "1rem" : "1.2rem"};
        --timeline-logo-to-body-gap: ${props => isChildTimeline(props.timelineDepth) ? "1.6rem" : "2rem"};
        --timeline-logo-pointer-size: 1.6rem;
    `}
`;

const Line = styled.div`
    position: absolute;
    background-color: var(--timeline-line-color);
    margin-left: calc(var(--timeline-left-margin) + (var(--timeline-logo-width) - var(--timeline-line-width)) / 2);
    width: var(--timeline-line-width);
    margin-top: calc(-1 * var(--timeline-items-vertical-gap));
    height: calc(var(--timeline-items-vertical-gap) + var(--timeline-logo-top-margin));
    z-index: 0;
`;
